import type { SocialSharing } from '@change/ds-core-ui';
import { EmailOutlineIcon } from '@change/ds-icons/EmailOutlineIcon';
import { FacebookIcon } from '@change/ds-icons/FacebookIcon';
import { FacebookMessengerIcon } from '@change/ds-icons/FacebookMessengerIcon';
import { InstagramIcon } from '@change/ds-icons/InstagramIcon';
import { LineIcon } from '@change/ds-icons/LineIcon';
import { LinkIcon } from '@change/ds-icons/LinkIcon';
import { NextDoorIcon } from '@change/ds-icons/NextDoorIcon';
import { RedditIcon } from '@change/ds-icons/RedditIcon';
import { ShareIcon } from '@change/ds-icons/ShareIcon';
import { SmsIcon } from '@change/ds-icons/SmsIcon';
import { TikTokIcon } from '@change/ds-icons/TikTokIcon';
import { TwitterIcon } from '@change/ds-icons/TwitterIcon';
import { VkIcon } from '@change/ds-icons/VkIcon';
import { WhatsAppIcon } from '@change/ds-icons/WhatsAppIcon';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';

import type { ShareChannel as PageShareChannel } from './types';

export type ShareVariant = NonNullable<React.ComponentProps<typeof SocialSharing>['variant']>;

export type TranslateLabel = (i18n: I18nUtilities) => string;

export type ShareButtonConfig = Readonly<{
	icon: React.ReactElement;
	label: TranslateLabel;
	variant?: ShareVariant;
	successLabel?: TranslateLabel;
	secondaryLabel?: TranslateLabel;
}>;

const CHANNEL_ICONS = {
	facebook: FacebookIcon,
	instagram: InstagramIcon,
	twitter: TwitterIcon,
	whatsapp: WhatsAppIcon,
	facebookMessenger: FacebookMessengerIcon,
	nextdoor: NextDoorIcon,
	reddit: RedditIcon,
	line: LineIcon,
	tiktok: TikTokIcon,
	vk: VkIcon,
	email: EmailOutlineIcon,
	sms: SmsIcon,
	link: LinkIcon,
	share: ShareIcon,
} as const;

const createShareButtonConfig = (
	iconKey: keyof typeof CHANNEL_ICONS,
	label: TranslateLabel,
	variant: ShareVariant = 'default',
	options?: {
		successLabel?: TranslateLabel;
		secondaryLabel?: TranslateLabel;
		iconProps?: Record<string, unknown>;
	},
): ShareButtonConfig => {
	const IconComponent = CHANNEL_ICONS[iconKey];
	return {
		icon: <IconComponent {...(options?.iconProps || {})} />,
		label,
		variant,
		...(options?.successLabel && { successLabel: options.successLabel }),
		...(options?.secondaryLabel && { secondaryLabel: options.secondaryLabel }),
	};
};

export const SHARE_BUTTON_CONFIG: Readonly<Record<PageShareChannel, ShareButtonConfig>> = {
	facebook: createShareButtonConfig(
		'facebook',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.short.facebook'),
		'facebook',
	),
	facebookMessenger: createShareButtonConfig(
		'facebookMessenger',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.short.facebook_messenger'),
		'facebookMessenger',
	),
	twitter: createShareButtonConfig(
		'twitter',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.short.twitter'),
		'twitter',
	),
	whatsapp: createShareButtonConfig(
		'whatsapp',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.short.whatsapp'),
		'whatsapp',
	),
	instagram: createShareButtonConfig(
		'instagram',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.short.instagram'),
		'instagram',
	),
	line: createShareButtonConfig('line', (i18n) => i18n.translate('fe.components.psf.share.buttons.short.line')),
	email: createShareButtonConfig('email', (i18n) => i18n.translate('fe.components.psf.share.buttons.short.email')),
	sms: createShareButtonConfig('sms', (i18n) => i18n.translate('fe.components.psf.share.buttons.short.sms')),
	copy: createShareButtonConfig(
		'link',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.copy_link'),
		'default',
		{
			successLabel: (i18n) => i18n.translate('fe.components.share_buttons.copied'),
			secondaryLabel: (i18n) => i18n.translate('corgi.psf.share.buttons.share_link_label'),
		},
	),
	vk: createShareButtonConfig('vk', (i18n) => i18n.translate('fe.components.psf.share.buttons.short.vk')),
	nextdoor: createShareButtonConfig(
		'nextdoor',
		(i18n) => i18n.translate('corgi.psf.share.buttons.short.nextdoor'),
		'nextdoor',
	),
	mobileNativeShare: createShareButtonConfig('share', (i18n) =>
		i18n.translate('corgi.psf.share.buttons.short.mobile_share'),
	),
	reddit: createShareButtonConfig('reddit', (i18n) => i18n.translate('corgi.psf.share.buttons.short.reddit'), 'reddit'),
	instagramStories: createShareButtonConfig(
		'instagram',
		(i18n) => i18n.translate('fe.components.psf.share.buttons.short.instagram'),
		'instagram',
	),
	tiktok: createShareButtonConfig('tiktok', (i18n) => i18n.translate('corgi.psf.share.buttons.short.tiktok'), 'tiktok'),
};
